import {
  extendLocaleRecord,
  type LegacyLocale,
  type Locale,
} from "../translations";
import type {
  Product,
  ProductBenefitKey,
  ProductFamilyId,
  TechnicalValue,
} from "./catalog";

const legacyFamilyOverview: Record<
  LegacyLocale,
  Record<ProductFamilyId, string>
> = {
  en: {
    "bisalloy-wear":
      "A quenched and tempered abrasion-resistant grade in the BISALLOY® Wear range. Selection should follow the verified hardness, thickness and fabrication requirements of the application.",
    "bisalloy-structural":
      "A high-strength quenched and tempered structural grade intended for weight-sensitive and load-bearing fabrication. Use the verified thickness-dependent values when selecting a grade.",
    "bisalloy-armour":
      "An impact-resistant armour grade developed for military and ballistic protection applications. Final grade and thickness selection requires threat-specific engineering review.",
    "bisalloy-protection":
      "A quenched and tempered protection grade for blast, penetration and security applications. The published mechanical values are reference values from official manufacturer sources.",
    "other-high-strength":
      "A Q700D low-alloy, high-strength structural plate listed on Bisalloy Global for lighter load-bearing and transport structures. Published values are verified against the current official product page and the official BISALLOY product datasheet.",
  },
  tr: {
    "bisalloy-wear":
      "BISALLOY® Wear serisinde su verilmiş ve temperlenmiş aşınmaya dayanıklı bir kalitedir. Seçim, uygulamanın doğrulanmış sertlik, kalınlık ve imalat gereksinimlerine göre yapılmalıdır.",
    "bisalloy-structural":
      "Ağırlığa duyarlı ve yük taşıyan imalatlar için yüksek dayanımlı, su verilmiş ve temperlenmiş yapısal çelik kalitesidir. Kalite seçiminde doğrulanmış, kalınlığa bağlı değerler esas alınmalıdır.",
    "bisalloy-armour":
      "Askerî ve balistik koruma uygulamaları için geliştirilmiş darbe dayanımlı zırh çeliği kalitesidir. Nihai kalite ve kalınlık seçimi tehdide özel mühendislik değerlendirmesi gerektirir.",
    "bisalloy-protection":
      "Patlama, nüfuziyet ve güvenlik uygulamalarına yönelik su verilmiş ve temperlenmiş koruma çeliği kalitesidir. Gösterilen mekanik değerler resmî üretici kaynaklarındaki referans değerlerdir.",
    "other-high-strength":
      "Bisalloy Global’de daha hafif yük taşıyan ve taşımacılık yapıları için listelenen düşük alaşımlı, yüksek dayanımlı Q700D yapı levhasıdır. Yayımlanan değerler güncel resmî ürün sayfası ve resmî BISALLOY ürün veri föyüyle doğrulanmıştır.",
  },
  ru: {
    "bisalloy-wear":
      "Закалённая и отпущенная износостойкая марка линейки BISALLOY® Wear. Выбор должен учитывать подтверждённые значения твёрдости, толщины и требования к изготовлению.",
    "bisalloy-structural":
      "Высокопрочная закалённая и отпущенная конструкционная марка для несущих и чувствительных к массе конструкций. При выборе учитывайте подтверждённые значения, зависящие от толщины.",
    "bisalloy-armour":
      "Ударостойкая броневая марка для военных и баллистических применений. Окончательный выбор марки и толщины требует инженерной оценки конкретной угрозы.",
    "bisalloy-protection":
      "Закалённая и отпущенная защитная марка для противовзрывных, противопробивных и охранных применений. Механические значения приведены по официальным источникам производителя.",
    "other-high-strength":
      "Низколегированный высокопрочный конструкционный лист Q700D, представленный Bisalloy Global для облегчённых несущих и транспортных конструкций. Опубликованные значения сверены с актуальной официальной страницей продукта и официальным техническим паспортом BISALLOY.",
  },
  kk: {
    "bisalloy-wear":
      "BISALLOY® Wear желісіндегі шынықтырылған және босатылған тозуға төзімді марка. Таңдау расталған қаттылық, қалыңдық және дайындау талаптарына негізделуі тиіс.",
    "bisalloy-structural":
      "Салмаққа сезімтал және жүк көтеретін конструкциялар үшін жоғары берікті шынықтырылған құрылымдық марка. Таңдау кезінде қалыңдыққа тәуелді расталған мәндерді пайдаланыңыз.",
    "bisalloy-armour":
      "Әскери және баллистикалық қорғанысқа арналған соққыға төзімді сауыт маркасы. Марка мен қалыңдықты түпкілікті таңдау нақты қауіпке инженерлік бағалауды талап етеді.",
    "bisalloy-protection":
      "Жарылыс, ену және қауіпсіздік қолданбаларына арналған шынықтырылған қорғаныс маркасы. Механикалық мәндер өндірушінің ресми көздерінен алынған.",
    "other-high-strength":
      "Bisalloy Global жеңіл жүк көтергіш және көлік конструкцияларына ұсынған төмен легірленген, жоғары берікті Q700D құрылымдық табағы. Жарияланған мәндер қазіргі ресми өнім бетімен және BISALLOY ресми өнім деректер парағымен расталған.",
  },
  ky: {
    "bisalloy-wear":
      "BISALLOY® Wear катарындагы сугарылып жумшартылган, эскирүүгө туруктуу марка. Тандоо ырасталган катуулук, калыңдык жана даярдоо талаптарына негизделиши керек.",
    "bisalloy-structural":
      "Салмакка сезимтал жана жүк көтөрүүчү конструкциялар үчүн жогорку бекемдиктеги структуралык марка. Тандоодо калыңдыкка жараша ырасталган маанилерди колдонуңуз.",
    "bisalloy-armour":
      "Аскердик жана баллистикалык коргоо үчүн соккуга туруктуу соот маркасы. Марка менен калыңдыкты акыркы тандоо коркунучка ылайык инженердик баалоону талап кылат.",
    "bisalloy-protection":
      "Жарылуу, тешип өтүү жана коопсуздук колдонмолору үчүн коргоочу болот маркасы. Механикалык маанилер өндүрүүчүнүн расмий булактарынан алынган.",
    "other-high-strength":
      "Bisalloy Global жеңил жүк көтөрүүчү жана транспорттук конструкциялар үчүн көрсөткөн аз легирленген, жогорку бекемдиктеги Q700D конструкциялык табагы. Жарыяланган маанилер учурдагы расмий продукт барагы жана BISALLOY расмий продукт маалымат барагы менен ырасталган.",
  },
  uz: {
    "bisalloy-wear":
      "BISALLOY® Wear turkumidagi toblangan va bo‘shatilgan, yeyilishga chidamli marka. Tanlov tasdiqlangan qattiqlik, qalinlik va ishlab chiqarish talablariga asoslanishi kerak.",
    "bisalloy-structural":
      "Og‘irlikka sezgir va yuk ko‘taruvchi konstruksiyalar uchun yuqori mustahkamlikdagi konstruksion marka. Tanlashda qalinlikka bog‘liq tasdiqlangan qiymatlardan foydalaning.",
    "bisalloy-armour":
      "Harbiy va ballistik himoya uchun zarbaga chidamli zirh markasi. Marka va qalinlikni yakuniy tanlash tahdidga xos muhandislik bahosini talab qiladi.",
    "bisalloy-protection":
      "Portlash, kirib borish va xavfsizlik qo‘llanmalari uchun himoya po‘lati markasi. Mexanik qiymatlar ishlab chiqaruvchining rasmiy manbalaridan olingan.",
    "other-high-strength":
      "Bisalloy Global yengil yuk ko‘taruvchi va transport konstruksiyalari uchun ko‘rsatgan kam legirlangan, yuqori mustahkamlikdagi Q700D konstruksion listidir. E’lon qilingan qiymatlar amaldagi rasmiy mahsulot sahifasi va BISALLOY rasmiy mahsulot ma’lumotlar varag‘i bilan tasdiqlangan.",
  },
  tg: {
    "bisalloy-wear":
      "Навъи обутобёфта ва фарсудашавиитобовари силсилаи BISALLOY® Wear. Интихоб бояд ба сахтӣ, ғафсӣ ва талаботи тасдиқшудаи коркард асос ёбад.",
    "bisalloy-structural":
      "Навъи сохтории пурмустаҳкам барои конструксияҳои борбардор ва ҳассос ба вазн. Ҳангоми интихоб қиматҳои тасдиқшудаи вобаста ба ғафсиро истифода баред.",
    "bisalloy-armour":
      "Навъи зиреҳии зарбатобовар барои ҳифзи ҳарбӣ ва баллистикӣ. Интихоби ниҳоии навъ ва ғафсӣ арзёбии муҳандисии таҳдиди мушаххасро талаб мекунад.",
    "bisalloy-protection":
      "Навъи пӯлоди муҳофизатӣ барои таркиш, нуфуз ва амният. Қиматҳои механикӣ аз манбаъҳои расмии истеҳсолкунанда гирифта шудаанд.",
    "other-high-strength":
      "Варақи сохтории камхӯла ва пурмустаҳками Q700D мебошад, ки Bisalloy Global барои сохторҳои сабуки борбардор ва нақлиётӣ пешниҳод мекунад. Қиматҳои нашршуда бо саҳифаи ҷории расмии маҳсулот ва варақаи расмии маълумоти маҳсулоти BISALLOY тасдиқ шудаанд.",
  },
  tk: {
    "bisalloy-wear":
      "BISALLOY® Wear hataryndaky suwa düşürilip taplanan, könelmä çydamly görnüşdir. Saýlaw tassyklanan gatylyk, galyňlyk we önümçilik talaplaryna esaslanmalydyr.",
    "bisalloy-structural":
      "Agrama duýgur we ýük göteriji gurluşlar üçin ýokary berklikli gurluş görnüşidir. Saýlawda galyňlyga bagly tassyklanan bahalary ulanyň.",
    "bisalloy-armour":
      "Harby we ballistik gorag üçin urga çydamly sowut görnüşidir. Görnüşiň we galyňlygyň soňky saýlawy howpa degişli inženerçilik bahalandyryşyny talap edýär.",
    "bisalloy-protection":
      "Partlama, aralaşma we howpsuzlyk üçin gorag polat görnüşidir. Mehaniki bahalar öndürijiniň resmi çeşmelerinden alyndy.",
    "other-high-strength":
      "Bisalloy Global tarapyndan ýeňil ýük göteriji we ulag gurluşlary üçin görkezilen pes legirlenen, ýokary berklikli Q700D gurluş listidir. Çap edilen bahalar häzirki resmi önüm sahypasy we BISALLOY-yň resmi önüm maglumat sahypasy bilen tassyklanyldy.",
  },
};

const familyOverview = extendLocaleRecord<
  Record<ProductFamilyId, string>
>(
  legacyFamilyOverview,
);

const legacyBenefitLabels: Record<
  LegacyLocale,
  Record<ProductBenefitKey, string>
> = {
  en: {
    "verified-wear-resistance": "Verified abrasion-resistant hardness for wear-intensive service",
    "high-strength-efficiency": "High-strength grade selection for weight-sensitive load-bearing design",
    "ballistic-protection": "Purpose-developed properties for ballistic protection applications",
    "blast-impact-resistance": "Grade-specific resistance to blast, shock or penetration",
    "formability-weldability": "Family-level fabrication guidance is available; confirm grade-specific bending and welding limits",
    "grade-specific-selection": "Multiple grades allow selection around the application and verified technical range",
  },
  tr: {
    "verified-wear-resistance": "Yoğun aşınmalı çalışma koşulları için doğrulanmış aşınma dayanımlı sertlik",
    "high-strength-efficiency": "Ağırlığa duyarlı yük taşıyan tasarımlar için yüksek dayanımlı kalite seçimi",
    "ballistic-protection": "Balistik koruma uygulamaları için özel geliştirilmiş özellikler",
    "blast-impact-resistance": "Kaliteye özgü patlama, şok veya nüfuziyet dayanımı",
    "formability-weldability": "Aile düzeyinde imalat rehberi vardır; kaliteye özel büküm ve kaynak sınırlarını doğrulayın",
    "grade-specific-selection": "Farklı kalitelerle uygulama ve doğrulanmış teknik aralığa göre seçim",
  },
  ru: {
    "verified-wear-resistance": "Подтверждённая износостойкая твёрдость для интенсивного износа",
    "high-strength-efficiency": "Высокопрочный выбор для несущих конструкций с ограничением массы",
    "ballistic-protection": "Специально разработанные свойства для баллистической защиты",
    "blast-impact-resistance": "Стойкость марки к взрыву, удару или пробитию",
    "formability-weldability": "Доступны рекомендации для семейства; уточняйте пределы гибки и сварки конкретной марки",
    "grade-specific-selection": "Выбор марки по применению и подтверждённому техническому диапазону",
  },
  kk: {
    "verified-wear-resistance": "Қарқынды тозуға арналған расталған тозуға төзімді қаттылық",
    "high-strength-efficiency": "Салмағы шектеулі жүк көтеретін құрылымға жоғары берікті марка таңдау",
    "ballistic-protection": "Баллистикалық қорғанысқа арнайы әзірленген қасиеттер",
    "blast-impact-resistance": "Маркаға тән жарылысқа, соққыға немесе енуге төзімділік",
    "formability-weldability": "Топ деңгейіндегі дайындау нұсқаулығы бар; нақты марканың иілу және дәнекерлеу шектерін растаңыз",
    "grade-specific-selection": "Қолданба мен расталған техникалық ауқымға сай марка таңдау",
  },
  ky: {
    "verified-wear-resistance": "Катуу эскирүү үчүн ырасталган эскирүүгө туруктуу катуулук",
    "high-strength-efficiency": "Салмагы чектелген жүк көтөрүүчү түзүлүш үчүн жогорку бекемдик",
    "ballistic-protection": "Баллистикалык коргоо үчүн атайын иштелген касиеттер",
    "blast-impact-resistance": "Маркага ылайык жарылууга, соккуга же тешүүгө туруктуулук",
    "formability-weldability": "Үй-бүлө деңгээлиндеги даярдоо көрсөтмөсү бар; маркага тиешелүү ийүү жана ширетүү чектерин тактаңыз",
    "grade-specific-selection": "Колдонмого жана ырасталган техникалык аралыкка ылайык тандоо",
  },
  uz: {
    "verified-wear-resistance": "Kuchli yeyilish sharoiti uchun tasdiqlangan yeyilishga chidamli qattiqlik",
    "high-strength-efficiency": "Og‘irligi cheklangan yuk ko‘taruvchi dizayn uchun yuqori mustahkamlik",
    "ballistic-protection": "Ballistik himoya uchun maxsus ishlab chiqilgan xususiyatlar",
    "blast-impact-resistance": "Markaga xos portlash, zarba yoki kirib borishga qarshilik",
    "formability-weldability": "Oila darajasidagi tayyorlash qo‘llanmasi mavjud; markaga xos bukish va payvandlash chegaralarini tasdiqlang",
    "grade-specific-selection": "Qo‘llanma va tasdiqlangan texnik oraliq bo‘yicha marka tanlash",
  },
  tg: {
    "verified-wear-resistance": "Сахтии тасдиқшудаи фарсудашавиитобовар барои хизмати вазнин",
    "high-strength-efficiency": "Интихоби пурмустаҳкам барои конструксияи борбардори сабук",
    "ballistic-protection": "Хосиятҳои махсус барои ҳифзи баллистикӣ",
    "blast-impact-resistance": "Муқовимати навъ ба таркиш, зарба ё нуфуз",
    "formability-weldability": "Роҳнамои коркарди сатҳи оила мавҷуд аст; ҳудуди хамкунӣ ва кафшери навъи мушаххасро тасдиқ кунед",
    "grade-specific-selection": "Интихоби навъ аз рӯйи истифода ва доираи техникии тасдиқшуда",
  },
  tk: {
    "verified-wear-resistance": "Agyr könelme üçin tassyklanan könelmä çydamly gatylyk",
    "high-strength-efficiency": "Agramy çäkli ýük göteriji dizaýn üçin ýokary berklik",
    "ballistic-protection": "Ballistik gorag üçin ýörite işlenip düzülen häsiýetler",
    "blast-impact-resistance": "Görnüşe mahsus partlama, urgy ýa-da aralaşma garşylygy",
    "formability-weldability": "Maşgala derejesindäki taýýarlama görkezmesi bar; görnüşe mahsus egmek we kebşirlemek çäklerini tassyklaň",
    "grade-specific-selection": "Ulanyşa we tassyklanan tehniki aralyga görä görnüş saýlawy",
  },
};

const benefitLabels = extendLocaleRecord<
  Record<ProductBenefitKey, string>
>(
  legacyBenefitLabels,
);

type TechnicalNoteKey =
  | "typical"
  | "typical12"
  | "guaranteed"
  | "thicknessDependent"
  | "familyRange"
  | "approximate"
  | "subsizeImpact"
  | "sourceConflict";

const legacyTechnicalNoteLabels: Record<
  LegacyLocale,
  Record<TechnicalNoteKey, string>
> = {
  en: {
    typical: "Typical reference value in the official source.",
    typical12: "Typical value for a 12 mm plate in the official source.",
    guaranteed: "Guaranteed or minimum value in the official source.",
    thicknessDependent:
      "The value changes with thickness; use the source ranges.",
    familyRange:
      "Family-level range; product-specific size availability must be confirmed.",
    approximate:
      "Approximate nominal value; excluded from numeric filtering.",
    subsizeImpact:
      "Below 12 mm, the source uses proportional subsize Charpy V specimens.",
    sourceConflict:
      "Official sources conflict. The dated source is shown; human technical review is required.",
  },
  tr: {
    typical: "Resmî kaynaktaki tipik referans değerdir.",
    typical12: "Resmî kaynakta 12 mm levha için verilen tipik değerdir.",
    guaranteed: "Resmî kaynaktaki garanti veya asgari değerdir.",
    thicknessDependent:
      "Değer kalınlığa göre değişir; kaynak aralıklarını esas alın.",
    familyRange:
      "Ürün ailesi aralığıdır; ürüne özel ölçü bulunabilirliği doğrulanmalıdır.",
    approximate:
      "Yaklaşık nominal değerdir; sayısal filtre eşleşmesine katılmaz.",
    subsizeImpact:
      "12 mm altında kaynakta orantılı alt boyutlu Charpy V numuneleri kullanılır.",
    sourceConflict:
      "Resmî kaynaklar çelişiyor. Tarihli kaynak gösterilmiştir; insan teknik incelemesi gereklidir.",
  },
  ru: {
    typical: "Типичное справочное значение из официального источника.",
    typical12:
      "Типичное значение для листа 12 мм в официальном источнике.",
    guaranteed:
      "Гарантированное или минимальное значение из официального источника.",
    thicknessDependent:
      "Значение зависит от толщины; используйте диапазоны источника.",
    familyRange:
      "Диапазон семейства; доступность размеров конкретной марки требует подтверждения.",
    approximate:
      "Приблизительное номинальное значение; исключено из числовой фильтрации.",
    subsizeImpact:
      "При толщине менее 12 мм источник использует пропорциональные уменьшенные образцы Charpy V.",
    sourceConflict:
      "Официальные источники противоречат друг другу. Показан датированный источник; требуется техническая проверка специалистом.",
  },
  kk: {
    typical: "Ресми дереккөздегі типтік анықтамалық мән.",
    typical12: "Ресми дереккөздегі 12 мм табаққа арналған типтік мән.",
    guaranteed: "Ресми дереккөздегі кепілдендірілген немесе ең аз мән.",
    thicknessDependent:
      "Мән қалыңдыққа байланысты өзгереді; дереккөз ауқымдарын пайдаланыңыз.",
    familyRange:
      "Өнім тобының ауқымы; нақты марка өлшемдерінің қолжетімділігі расталуы тиіс.",
    approximate:
      "Шамамен берілген номинал мән; сандық сүзгіге қосылмайды.",
    subsizeImpact:
      "12 мм-ден төмен қалыңдықта дереккөз пропорционалды кішірейтілген Charpy V үлгілерін қолданады.",
    sourceConflict:
      "Ресми дереккөздер қайшы келеді. Күні көрсетілген дереккөз берілді; маманның техникалық тексеруі қажет.",
  },
  ky: {
    typical: "Расмий булактагы типтүү маалымдама мааниси.",
    typical12: "Расмий булактагы 12 мм табак үчүн типтүү маани.",
    guaranteed: "Расмий булактагы кепилденген же эң төмөнкү маани.",
    thicknessDependent:
      "Маани калыңдыкка жараша өзгөрөт; булактагы аралыктарды колдонуңуз.",
    familyRange:
      "Өнүм үй-бүлөсүнүн аралыгы; белгилүү марканын өлчөм жеткиликтүүлүгү ырасталышы керек.",
    approximate:
      "Болжолдуу номинал маани; сандык чыпкага кошулбайт.",
    subsizeImpact:
      "12 ммден төмөн калыңдыкта булак пропорционалдуу кичирейтилген Charpy V үлгүлөрүн колдонот.",
    sourceConflict:
      "Расмий булактар карама-каршы. Датасы бар булак көрсөтүлдү; адистин техникалык текшерүүсү талап кылынат.",
  },
  uz: {
    typical: "Rasmiy manbadagi tipik ma’lumot qiymati.",
    typical12: "Rasmiy manbada 12 mm list uchun berilgan tipik qiymat.",
    guaranteed: "Rasmiy manbadagi kafolatlangan yoki eng kichik qiymat.",
    thicknessDependent:
      "Qiymat qalinlikka qarab o‘zgaradi; manbadagi oraliqlardan foydalaning.",
    familyRange:
      "Mahsulot oilasi oralig‘i; aniq marka o‘lchamlari mavjudligi tasdiqlanishi kerak.",
    approximate:
      "Taxminiy nominal qiymat; raqamli filtrlashga kiritilmaydi.",
    subsizeImpact:
      "12 mm dan yupqa listlar uchun manbada mutanosib kichik Charpy V namunalari ishlatiladi.",
    sourceConflict:
      "Rasmiy manbalar o‘zaro zid. Sanasi ko‘rsatilgan manba berildi; mutaxassisning texnik tekshiruvi zarur.",
  },
  tg: {
    typical: "Қимати намунавии истинодӣ аз манбаи расмӣ.",
    typical12: "Қимати намунавӣ барои варақи 12 мм дар манбаи расмӣ.",
    guaranteed: "Қимати кафолатшуда ё ҳадди ақал аз манбаи расмӣ.",
    thicknessDependent:
      "Қимат аз ғафсӣ вобаста аст; доираҳои манбаъро истифода баред.",
    familyRange:
      "Доираи оилаи маҳсулот; дастрасии андозаи навъи мушаххас бояд тасдиқ шавад.",
    approximate:
      "Қимати тахминии номиналӣ; аз филтри рақамӣ хориҷ шудааст.",
    subsizeImpact:
      "Барои ғафсии камтар аз 12 мм манбаъ намунаҳои мутаносиби хурди Charpy V-ро истифода мебарад.",
    sourceConflict:
      "Манбаъҳои расмӣ мухолифанд. Манбаи дорои сана нишон дода шудааст; санҷиши техникии мутахассис зарур аст.",
  },
  tk: {
    typical: "Resmi çeşmedäki adaty salgylanma baha.",
    typical12: "Resmi çeşmede 12 mm list üçin berlen adaty baha.",
    guaranteed: "Resmi çeşmedäki kepillendirilen ýa-da iň pes baha.",
    thicknessDependent:
      "Baha galyňlyga görä üýtgeýär; çeşmedäki aralyklary ulanyň.",
    familyRange:
      "Önüm maşgalasynyň aralygy; anyk görnüşiň ölçeg elýeterliligi tassyklanmalydyr.",
    approximate:
      "Takmynan nominal baha; sanly süzgüçden çykarylýar.",
    subsizeImpact:
      "12 mm-den ýuka listler üçin çeşmede proporsional kiçi Charpy V nusgalary ulanylýar.",
    sourceConflict:
      "Resmi çeşmeler biri-birine garşy gelýär. Seneli çeşme görkezildi; hünärmeniň tehniki barlagy zerurdyr.",
  },
};

const technicalNoteLabels = extendLocaleRecord<
  Record<TechnicalNoteKey, string>
>(
  legacyTechnicalNoteLabels,
);

export function getProductOverview(locale: Locale, product: Product) {
  return `${product.canonicalName}: ${familyOverview[locale][product.familyId]}`;
}

export function getFamilyOverview(
  locale: Locale,
  familyId: ProductFamilyId,
) {
  return familyOverview[locale][familyId];
}

export function getBenefitLabel(locale: Locale, benefit: ProductBenefitKey) {
  return benefitLabels[locale][benefit];
}

export function getTechnicalNotes(locale: Locale, value: TechnicalValue) {
  const noteKeys: TechnicalNoteKey[] = [];
  const qualifier = value.qualifier ?? "";
  const normalizedQualifier = qualifier.toLowerCase();

  if (value.verificationStatus === "needs-human-review") {
    noteKeys.push("sourceConflict");
  }
  if (qualifier.startsWith("Typical for 12 mm plate")) {
    noteKeys.push("typical12");
  } else if (
    qualifier === "Typical" ||
    qualifier.startsWith("Typical reference")
  ) {
    noteKeys.push("typical");
  }
  if (qualifier.startsWith("Guaranteed")) {
    noteKeys.push("guaranteed");
  }
  if (
    normalizedQualifier.includes("thickness-dependent") ||
    normalizedQualifier.includes("thickness groups") ||
    normalizedQualifier.includes("varies by thickness")
  ) {
    noteKeys.push("thicknessDependent");
  }
  if (normalizedQualifier.includes("family")) {
    noteKeys.push("familyRange");
  }
  if (qualifier.startsWith("Approximate")) {
    noteKeys.push("approximate");
  }
  if (normalizedQualifier.includes("subsize charpy")) {
    noteKeys.push("subsizeImpact");
  }

  return [...new Set(noteKeys)].map(
    (noteKey) => technicalNoteLabels[locale][noteKey],
  );
}

export function getSourceConflictNotice(locale: Locale) {
  return technicalNoteLabels[locale].sourceConflict;
}
